import type { DragEvent } from 'react';

/**
 * A step's tasks by the phase they are done in (design study 31, slice 4, q-r2-early A; Jason, 2026-10-29: "a very
 * simple way to add tasks to a node in each of other phases without too much clicking and selecting"): the earlier
 * phases first, each "Done in …", the step's own phase last, "In …"; each group with a line to type a task into; an
 * earlier phase with no group yet one click away; a task set to another phase by its Done in, or dragged into another
 * group as a shortcut, done in that phase. A
 * task is done in its step's phase or an earlier one, never a later one (core `TASK_PHASE_RULE`): one in a later phase,
 * or in none, is in its step's. One rule for the master's card (`NodeCard`) and a job's panel (`StepPanel`'s Tasks),
 * drawn by one list (`TaskGroups`).
 */

export interface TaskPhase<K extends string> {
  key: K;
  name: string;
}

export interface TaskGroup<K extends string, T> {
  phase: K | null;
  name: string;
  /** The step's own phase: the last group, always there. */
  own: boolean;
  tasks: T[];
}

/** The type a task dragged from one group to another hands over: its id, or on the master its key. */
export const TASK_DRAG = 'application/x-nsb-task';

/** What a task's row carries while it can be dragged into another group. */
export interface TaskDrag {
  draggable: true;
  onDragStart: (e: DragEvent<HTMLElement>) => void;
}

const at = <K extends string>(phases: readonly TaskPhase<K>[], k: K | null): number => (k === null ? -1 : phases.findIndex((p) => p.key === k));

/**
 * A step's tasks grouped by the phase each is done in, in the order of its list: each earlier phase with a task in it
 * (or `open`, opened to type one into), then its own phase, always. A step in no phase of the tree (`own` null, or one
 * the tree does not have) has its list as one group, with no heading.
 */
export function taskGroups<K extends string, T>(
  phases: readonly TaskPhase<K>[],
  own: K | null,
  tasks: readonly T[],
  phaseOf: (t: T) => K | null,
  open: readonly K[] = [],
): TaskGroup<K, T>[] {
  const ownAt = at(phases, own);
  if (ownAt < 0) return [{ phase: null, name: '', own: true, tasks: [...tasks] }];
  const groups: TaskGroup<K, T>[] = [];
  const early = new Set<T>();
  for (const p of phases.slice(0, ownAt)) {
    const inIt = tasks.filter((t) => phaseOf(t) === p.key);
    for (const t of inIt) early.add(t);
    if (inIt.length || open.includes(p.key)) groups.push({ phase: p.key, name: p.name, own: false, tasks: inIt });
  }
  groups.push({ phase: own, name: phases[ownAt]!.name, own: true, tasks: tasks.filter((t) => !early.has(t)) });
  return groups;
}

/** The earlier phases than the step's own with no group yet: offered at the foot of its list, one click each. */
export function earlierToAdd<K extends string>(phases: readonly TaskPhase<K>[], own: K | null, groups: readonly TaskGroup<K, unknown>[]): TaskPhase<K>[] {
  const ownAt = at(phases, own);
  if (ownAt < 0) return [];
  const has = new Set(groups.map((g) => g.phase));
  return phases.slice(0, ownAt).filter((p) => !has.has(p.key));
}

/**
 * What a task's Done in offers (slice 4's final review, WCAG 2.1.1: moving a task is never drag alone): its step's own
 * phase and each earlier one, earliest first, as the rule allows (core `TASK_PHASE_RULE`). None for a step in no phase of
 * the tree, or in its first, where there is nowhere else to do it.
 */
export function doneInChoices<K extends string>(phases: readonly TaskPhase<K>[], own: K | null): TaskPhase<K>[] {
  const ownAt = at(phases, own);
  return ownAt > 0 ? phases.slice(0, ownAt + 1) : [];
}

/** The line under a list whose tasks can be moved: both ways, where a drag has another group to go to. */
export const moveHint = (dragging: boolean): string =>
  dragging ? 'Set Done in beside a task, or drag it into another group, to change the phase it is done in.' : 'Set Done in beside a task to change the phase it is done in.';

/** A group's heading: "Done in Design" for an earlier phase, "In Pre-construction" for its own; none for a step in no phase. */
export const groupHeading = (g: Pick<TaskGroup<string, unknown>, 'name' | 'own'>): string | null => (g.name ? (g.own ? `In ${g.name}` : `Done in ${g.name}`) : null);

/** What goes in a group's line: a task done in its phase. */
export const addWords = (name: string | null): string => (name ? `+ A task in ${name}…` : '+ A task…');

/** The line's name, read out. */
export const addLabel = (name: string | null): string => (name ? `A new task done in ${name}` : 'A new task on its list');
